import { useEffect } from 'react';

export const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://allchinabuyspread.hair';

const DEFAULT_KEYWORDS =
  'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  lang?: string;
  keywords?: string;
  ogTitle?: string;
  ogDesc?: string;
  ogUrl?: string;
  ogType?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDesc?: string;
  twitterImage?: string;
  structuredData?: Record<string, unknown>;
}

function resolveAbsoluteUrl(url?: string): string {
  if (!url) return SITE_URL;
  if (/^https?:\/\//.test(url)) return url;
  return `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

function setMeta(attr: 'name' | 'property', key: string, content?: string) {
  const selector = attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;

  if (content === undefined || content === '') {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function useSEO({
  title,
  description,
  canonical,
  lang,
  keywords,
  ogTitle,
  ogDesc,
  ogUrl,
  ogType,
  ogImage,
  twitterTitle,
  twitterDesc,
  twitterImage,
  structuredData,
}: SEOProps) {
  useEffect(() => {
    const absoluteCanonical = resolveAbsoluteUrl(canonical);
    const absoluteOgUrl = resolveAbsoluteUrl(ogUrl || canonical);

    document.title = title;

    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords || DEFAULT_KEYWORDS);
    setMeta('property', 'og:title', ogTitle || title);
    setMeta('property', 'og:description', ogDesc || description);
    setMeta('property', 'og:url', absoluteOgUrl);
    setMeta('property', 'og:type', ogType || 'website');
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', twitterTitle || ogTitle || title);
    setMeta('name', 'twitter:description', twitterDesc || ogDesc || description);
    setMeta('name', 'twitter:image', twitterImage || ogImage);

    setLink('canonical', absoluteCanonical);

    if (lang) {
      document.documentElement.lang = lang;
    }

    const existingLd = document.querySelector('script[type="application/ld+json"]');
    if (structuredData) {
      let scriptLd = existingLd as HTMLScriptElement | null;
      if (!scriptLd) {
        scriptLd = document.createElement('script');
        scriptLd.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptLd);
      }
      scriptLd.textContent = JSON.stringify(structuredData);
    } else if (existingLd) {
      existingLd.remove();
    }
  }, [
    title,
    description,
    canonical,
    lang,
    keywords,
    ogTitle,
    ogDesc,
    ogUrl,
    ogType,
    ogImage,
    twitterTitle,
    twitterDesc,
    twitterImage,
    structuredData,
  ]);
}